import {
  CloseDrawerButtonWithIcon,
  SubmitDrawerButtonWithIcon,
  TopButtonsGroup,
} from "@src/components/ContentDrawer/DrawerComponents";
import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ICreateScheduleForm } from "@src/types/schedules";
import { theme } from "@src/theme";
import AppSelectDropdown from "@src/components/AppSelectDropdown";
import { useMyShows } from "@src/hooks/useMyShows";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { useInfinitePlaylists } from "@src/queries/playlistQueries";
import DateTimePicker from "@src/components/DateTimePicker";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  setCreateEventForm,
  setUpdateEventForm,
} from "@src/redux/slices/scheduleSlice";
import {
  useCreateScheduleEventMutation,
  useUpdateScheduleEventMutation,
} from "@src/queries/scheduleQueries";
import { useNotification } from "@src/context/NotificationContext";
import { AxiosError, AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { IPlaylistRecord } from "@src/types/playlist";
import {
  AppDropdownItem,
  AppSelectDropdownOption,
} from "@src/components/AppSelectDropdown/AppSelectDropdown";
import { DotIcon, TrashIcon } from "lucide-react";
import { UIButton } from "@src/components/UIButton";
import httpApi from "@src/utils/httpApi";
import UIModal from "@src/components/UIModal/UIModal";
import useModal from "@src/hooks/useModal";
import Spinner from "@src/components/Spinner";

const schema = yup
  .object({
    endTime: yup.string().required("End time is required"),
    startTime: yup.string().required("Start time is required"),
    playlistId: yup.string().optional(),
    showId: yup.string().optional(),
    moodId: yup.string().optional(),
  })
  .required();
const defaultValues: ICreateScheduleForm = {
  endTime: "",
  startTime: "",
  playlistId: undefined,
  showId: undefined,
  moodId: undefined,
};
const AddNewSchedule = ({
  refetchEvents,
  onClose,
}: {
  refetchEvents: () => void;
  onClose: () => void;
}) => {
  const createScheduleEvent = useCreateScheduleEventMutation();
  const updateScheduleEvent = useUpdateScheduleEventMutation();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotification();
  const { createEventForm, updateEventForm, isUpdateEventForm } =
    useAppSelector((state) => state.schedule);
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const roleType = useRole();
  const curator = useAuthCurator();
  const [searchInputs, setSearchInputs] = React.useState({
    show: "",
    playlist: "",
    mood: "",
  });
  const [searchText, setSearchText] = React.useState({
    show: "",
    playlist: "",
    mood: "",
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchText((prev) => ({
        ...prev,
        show: searchInputs.show,
        playlist: searchInputs.playlist,
      }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInputs]);
  const { myShows, loadingMyShows } = useMyShows({
    curatorId: curator!.id,
    roleType,
    searchText:
      createEventForm?.showId || updateEventForm?.showId || searchText.show,
  });
  const { data, isLoading: loadingPlaylists } = useInfinitePlaylists({
    playlistProcessedAudioNotNull: true,
    playlistProcessedAudioDurationGte: 7200,
    curatorId: "",
    role: roleType,
    showId: watch("showId") || createEventForm.showId || updateEventForm.showId,
    searchText:
      createEventForm?.playlistId ||
      updateEventForm?.playlistId ||
      searchText.playlist,
    sorting: {
      field: "createdAt",
      direction: "desc",
    },
  });
  const [selectedPlaylist, setSelectedPlaylist] =
    React.useState<IPlaylistRecord | null>(null);

  useEffect(() => {
    const playlistSelected = playlists.find(
      (playlist) => playlist.documentId === watch("playlistId")
    );
    if (
      playlistSelected?.documentId !== selectedPlaylist?.documentId &&
      playlistSelected?.show.documentId !== watch("showId")
    ) {
      setValue("showId", playlistSelected?.show.documentId ?? "");
      setSearchInputs((prev) => ({
        ...prev,
        show: String(playlistSelected?.show?.id || ""),
      }));
    }
    if (playlistSelected) {
      setSelectedPlaylist(playlistSelected);
    }
  }, [watch("playlistId")]);

  useEffect(() => {
    if (isUpdateEventForm) {
      setValue("showId", updateEventForm.showId);
      setValue("playlistId", updateEventForm.playlistId);
      setValue("endTime", updateEventForm.endTime);
      setValue("startTime", updateEventForm.startTime);
    } else {
      setValue("endTime", createEventForm.endTime);
      setValue("startTime", createEventForm.startTime);
      setValue("playlistId", createEventForm.playlistId || "");
      setValue("showId", createEventForm.showId || "");
    }
  }, [
    isUpdateEventForm,
    updateEventForm.endTime,
    updateEventForm.startTime,
    createEventForm.endTime,
    createEventForm.startTime,
  ]);

  const playlists = useMemo(
    () =>
      (
        (data?.pages as AxiosResponse<ApiResponse<IPlaylistRecord[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [],
    [data]
  );

  const onSubmit = async (data: ICreateScheduleForm) => {
    if (data.playlistId) {
      const selectedPlaylist = playlists.find(
        (playlist) => playlist.documentId === data.playlistId
      );
      if (
        !(
          selectedPlaylist?.playlistStatus === "submitted" ||
          selectedPlaylist?.playlistStatus === "aired" ||
          selectedPlaylist?.playlistStatus === "scheduled" ||
          selectedPlaylist?.playlistStatus === "archived"
        )
      ) {
        setError("playlistId", {
          message:
            "Playlist must be submitted, aired, archived or scheduled to create a schedule event",
        });
        addNotification(
          "Playlist must be submitted, aired, archived or scheduled to create a schedule event",
          "error"
        );
        return;
      }

      if (!selectedPlaylist?.playlistProcessedAudio?.url) {
        setError("playlistId", {
          message:
            "Playlist must have processed audio to create a schedule event",
        });
        addNotification(
          "Playlist must have processed audio to create a schedule event",
          "error"
        );
        return;
      }
    }
    if (isUpdateEventForm) {
      updateScheduleEvent.mutateAsync(
        {
          data: {
            ...data,
            scheduleId: updateEventForm.scheduleId,
          },
        },
        {
          onSuccess: () => {
            dispatch(setUpdateEventForm(defaultValues));
            addNotification("Schedule event updated successfully", "success");
            refetchEvents();
            onClose();
          },
          onError: () => {
            addNotification("Error updating schedule event", "error");
          },
        }
      );
      return;
    }
    await createScheduleEvent.mutateAsync(
      { data },
      {
        onSuccess: () => {
          dispatch(setCreateEventForm(defaultValues));
          addNotification("Schedule event created successfully", "success");
          refetchEvents();
          onClose();
        },
        onError: (err) => {
          if (err instanceof AxiosError) {
            if (err.response?.data?.error?.message?.includes("playlist")) {
              setError("playlistId", err.response?.data?.error?.message);
              addNotification(err.response?.data?.error?.message, "error");
              return;
            } else {
              addNotification("Error creating schedule event", "error");
            }
          }
          addNotification("Error creating schedule event", "error");
        },
      }
    );
    return;
  };
  useEffect(() => {
    return () => {
      reset();
      dispatch(setCreateEventForm(defaultValues));
    };
  }, []);

  const { closeModal, openModal, isOpen } = useModal();
  const onRemoveEvent = () => {
    openModal();
  };
  const [deleting, setDeleting] = React.useState(false);
  const onConfirmRemoveEvent = async () => {
    try {
      setDeleting(true);
      // Strapi hands the playlist back to the submitted queue when its schedule is deleted.
      await httpApi.delete(`api/schedules/${updateEventForm.scheduleId}`);
    } catch {
      addNotification("Error deleting schedule event", "error");
    } finally {
      setDeleting(false);
      closeModal();
      dispatch(setUpdateEventForm(defaultValues));
      refetchEvents();
      onClose();
    }
  };
  const resetPlaylistValue = () => {
    setSearchInputs((prev) => ({
      ...prev,
      playlist: "",
    }));
    if (isUpdateEventForm) {
      dispatch(setUpdateEventForm({ playlistId: "" }));
    } else {
      dispatch(setCreateEventForm({ playlistId: "" }));
    }
    setSelectedPlaylist(null);
    setValue("playlistId", "");
  };

  const resetShowValue = () => {
    setSearchInputs((prev) => ({
      ...prev,
      show: "",
    }));
    if (isUpdateEventForm) {
      dispatch(setUpdateEventForm({ showId: "" }));
    } else {
      dispatch(setCreateEventForm({ showId: "" }));
    }
    setValue("playlistId", "");
    setValue("showId", "");
    setSelectedPlaylist(null);
  };
  return (
    <>
      <UIModal
        title="Remove scheduled show"
        actions={[
          <UIButton key="cancel" onClick={closeModal} className="secondary">
            Cancel
          </UIButton>,
          <UIButton
            type="button"
            onClick={onConfirmRemoveEvent}
            variant="danger"
            disabled={deleting}
          >
            Confirm
          </UIButton>,
        ]}
        isOpen={isOpen}
        onClose={() => {
          closeModal();
        }}
      >
        {deleting ? (
          <div style={{ padding: theme.margins.medium }}>
            <Spinner small />
          </div>
        ) : (
          ""
        )}
        <div style={{ padding: theme.margins.medium }}>
          Are you sure you want to remove this scheduled show?
        </div>
        <div style={{ padding: theme.margins.medium }}>
          This action cannot be undone.
        </div>
      </UIModal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TopButtonsGroup>
          <CloseDrawerButtonWithIcon
            onClick={() => {
              onClose();
              reset();
            }}
            disabled={false}
          />
          <div
            style={{
              display: "flex",
              gap: theme.margins.medium,
            }}
          >
            {isUpdateEventForm && (
              <UIButton
                type="button"
                data-testid="remove-schedule-btn"
                onClick={onRemoveEvent}
                tooltipContent="Remove scheduled show"
                variant="transparent"
              >
                <TrashIcon color={theme.colors.red} size={20} />
              </UIButton>
            )}
            <SubmitDrawerButtonWithIcon disabled={false} />
          </div>
        </TopButtonsGroup>
        <div>
          <div style={{ marginBottom: theme.margins.medium }}>
            <Controller
              name="showId"
              control={control}
              render={({ field }) => (
                <AppSelectDropdown
                  disabled={loadingPlaylists || loadingMyShows}
                  searching={loadingMyShows}
                  setSearchText={(text) =>
                    setSearchInputs((prev) => ({
                      ...prev,
                      show: String(text || ""),
                    }))
                  }
                  searchText={searchInputs.show ?? ""}
                  options={myShows}
                  labelAccessor="title"
                  valueAccessor="documentId"
                  label={"Show"}
                  control={control}
                  resetValue={resetShowValue}
                  errors={errors}
                  {...field}
                  placeholder="Select show"
                  renderItem={(
                    option: AppSelectDropdownOption,
                    isSelected: boolean,
                    onSelect: () => void
                  ) => (
                    <AppDropdownItem
                      style={{ alignItems: "center", display: "flex" }}
                      onClick={onSelect}
                    >
                      {option.title}
                      <DotIcon />
                      {option.id}
                      {option?.curators?.[0]?.name ? (
                        <>
                          <DotIcon />
                          {option?.curators?.[0]?.name}
                        </>
                      ) : (
                        ""
                      )}
                    </AppDropdownItem>
                  )}
                />
              )}
            />
          </div>
          <div style={{ marginBottom: theme.margins.medium }}>
            <Controller
              name="playlistId"
              disabled={loadingPlaylists || loadingMyShows}
              control={control}
              render={({ field }) => (
                <AppSelectDropdown
                  searching={loadingPlaylists}
                  setSearchText={(text) =>
                    setSearchInputs((prev) => ({
                      ...prev,
                      playlist: String(text || ""),
                    }))
                  }
                  searchText={searchInputs.playlist ?? ""}
                  options={playlists}
                  resetValue={resetPlaylistValue}
                  labelAccessor="title"
                  valueAccessor="documentId"
                  label={"Playlist"}
                  control={control}
                  errors={errors}
                  {...field}
                  placeholder="Select playlist"
                  renderItem={(
                    option: AppSelectDropdownOption,
                    isSelected: boolean,
                    onSelect: () => void
                  ) => (
                    <AppDropdownItem
                      style={{ alignItems: "center", display: "flex" }}
                      onClick={onSelect}
                    >
                      {option.title}
                      <DotIcon />
                      {option.id}
                      {option?.curator?.name ? (
                        <>
                          <DotIcon />
                          {option?.curator?.name}
                        </>
                      ) : (
                        ""
                      )}
                    </AppDropdownItem>
                  )}
                />
              )}
            />
          </div>

          <div style={{ marginBottom: theme.margins.medium }}>
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label={"Start Time"}
                  control={control}
                  errors={errors}
                  {...field}
                  onDateChanged={(date) => {
                    setValue("startTime", date);
                    dispatch(setCreateEventForm({ startTime: date }));
                  }}
                />
              )}
            />
          </div>
          <div style={{ marginBottom: theme.margins.medium }}>
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  label={"End Time"}
                  control={control}
                  errors={errors}
                  {...field}
                  onDateChanged={(date) => {
                    setValue("endTime", date);
                    dispatch(setCreateEventForm({ endTime: date }));
                  }}
                />
              )}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default AddNewSchedule;
